import React, { useEffect, useMemo, useState } from "react";
import "./ReviewsModal.css";

const normalizeLocale = (locale) => {
  const v = String(locale || "").toLowerCase();
  return v === "kk" || v === "kz" ? "kk" : "ru";
};

const TEXTS = {
  kk: {
    reviews: "Пікірлер",
    myReview: "Менің пікірім",
    noReviews: "Әзірге пікір жоқ",
    noVisibleReviews: "Әзірге көрінетін пікір жоқ",
    save: "Сақтау",
    saving: "Сақталуда...",
    delete: "Жою",
    deleting: "Жойылуда...",
    placeholder: "Қысқаша әрі нақты жазыңыз...",
    distribution: "Ағымдағы бет бойынша үлестірім",
    noComment: "Пікірсіз",
    hidden: "Жасырын",
    visible: "Көрінеді",
    makeVisible: "Көрінетін ету",
    hide: "Жасыру",
    page: "Бет",
    of: "ішінен",
    prev: "← Алдыңғы",
    next: "Келесі →",
    user: "Пайдаланушы",
    reviewsCount: "пікір",
    needRating: "Алдымен баға қойыңыз",
    saveError: "Пікірді сақтау мүмкін болмады",
    deleteError: "Пікірді жою мүмкін болмады",
    toggleError: "Көріну күйін өзгерту мүмкін болмады",
    saveHandlerMissing: "Сақтау функциясы берілмеген",
  },
  ru: {
    reviews: "Отзывы",
    myReview: "Мой отзыв",
    noReviews: "Пока нет отзывов",
    noVisibleReviews: "Пока нет видимых отзывов",
    save: "Сохранить",
    saving: "Сохраняю...",
    delete: "Удалить",
    deleting: "Удаляю...",
    placeholder: "Напишите коротко и по делу...",
    distribution: "Распределение по текущей странице",
    noComment: "Без комментария",
    hidden: "Скрыт",
    visible: "Виден",
    makeVisible: "Сделать видимым",
    hide: "Скрыть",
    page: "Страница",
    of: "из",
    prev: "← Пред",
    next: "След →",
    user: "Пользователь",
    reviewsCount: "отзыв(ов)",
    needRating: "Сначала поставьте оценку",
    saveError: "Не удалось сохранить отзыв",
    deleteError: "Не удалось удалить отзыв",
    toggleError: "Не удалось изменить видимость",
    saveHandlerMissing: "Функция сохранения не передана",
  },
};

function Stars({ value = 0, onChange, readOnly = false, size = 18 }) {
  const v = Math.round((Number(value) || 0) * 2) / 2;

  return (
    <div className="rv-stars" style={{ gap: 6 }}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = v >= i;

        return (
          <button
            key={i}
            type="button"
            className={
              "rv-star" +
              (filled ? " is-filled" : "") +
              (readOnly ? " is-ro" : "")
            }
            style={{ fontSize: size }}
            onClick={() => {
              if (!readOnly) {
                onChange?.(i);
              }
            }}
            aria-label={`${i} stars`}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}

function safeText(r) {
  return (
    r?.review_text ??
    r?.comment ??
    r?.text ??
    r?.review ??
    r?.message ??
    r?.body ??
    ""
  );
}

function isHidden(r) {
  return Number(r?.is_hidden) === 1;
}

export default function ReviewsModal({
  locale = "kk",
  expert,
  summary,
  reviewsPage,
  reviews = [],
  myReview = null,
  canWrite = false,
  canManage = false,
  onSave,
  onDeleteMine,
  onToggleVisibility,
  onPageChange,
}) {
  const lang = normalizeLocale(locale);
  const t = TEXTS[lang];

  const list = useMemo(() => {
    if (reviewsPage?.data && Array.isArray(reviewsPage.data)) {
      return reviewsPage.data;
    }
    if (Array.isArray(reviews)) {
      return reviews;
    }
    return [];
  }, [reviewsPage, reviews]);

  const visibleList = useMemo(() => {
    return canManage ? list : list.filter((r) => !isHidden(r));
  }, [list, canManage]);

  const currentPage = Number(reviewsPage?.current_page || 1);
  const lastPage = Number(reviewsPage?.last_page || 1);

  const avg = Number(summary?.rating_avg || 0);
  const count = Number(summary?.rating_count || visibleList.length || 0);

  const buckets = useMemo(() => {
    const b = [0, 0, 0, 0, 0];

    visibleList.forEach((r) => {
      const k = Math.min(5, Math.max(1, Number(r?.rating) || 0));
      if (k) b[k - 1] += 1;
    });

    return { buckets: b, visibleCount: visibleList.length };
  }, [visibleList]);

  const [draftRating, setDraftRating] = useState(Number(myReview?.rating) || 0);
  const [draftText, setDraftText] = useState(safeText(myReview) || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggleId, setToggleId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraftRating(Number(myReview?.rating) || 0);
    setDraftText(safeText(myReview) || "");
    setError("");
  }, [myReview]);

  const canPrev = currentPage > 1;
  const canNext = currentPage < lastPage;

  const handleSave = async () => {
    if (!draftRating) {
      setError(t.needRating);
      return;
    }

    if (typeof onSave !== "function") {
      setError(t.saveHandlerMissing);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const text = String(draftText || "").trim();

      await onSave({
        rating: Number(draftRating),
        review_text: text,
        text,
        comment: text,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (typeof onDeleteMine !== "function") return;

    setDeleting(true);
    setError("");

    try {
      await onDeleteMine();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t.deleteError);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisibility = async (id, hidden) => {
    if (typeof onToggleVisibility !== "function") return;

    setToggleId(id);
    setError("");

    try {
      await onToggleVisibility(id, hidden ? true : false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t.toggleError);
    } finally {
      setToggleId(null);
    }
  };

  return (
    <div className="rv-wrap">
      <div className="rv-head">
        <div className="rv-title">
          {t.reviews}: <b>{expert?.name || "—"}</b>
        </div>

        {lastPage > 1 && (
          <div className="rv-mini-pager">
            <button
              className="rv-btn"
              disabled={!canPrev}
              onClick={() => onPageChange?.(currentPage - 1)}
              type="button"
            >
              ←
            </button>
            <div className="rv-mini-pager-info">
              {currentPage} / {lastPage}
            </div>
            <button
              className="rv-btn"
              disabled={!canNext}
              onClick={() => onPageChange?.(currentPage + 1)}
              type="button"
            >
              →
            </button>
          </div>
        )}
      </div>

      <div className="rv-summary">
        <div className="rv-avg">
          <div className="rv-avg-num">{avg ? avg.toFixed(1) : "—"}</div>
          <Stars value={avg} readOnly />
          <div className="rv-avg-sub">
            {count} {t.reviewsCount}
          </div>
        </div>

        <div className="rv-dist">
          {[5, 4, 3, 2, 1].map((st) => {
            const c = buckets.buckets[st - 1] || 0;
            const base = buckets.visibleCount || 0;
            const pct = base ? (c / base) * 100 : 0;

            return (
              <div key={st} className="rv-row">
                <div className="rv-row-l">{st}★</div>
                <div className="rv-bar">
                  <span style={{ width: `${pct}%` }} />
                </div>
                <div className="rv-row-r">{c}</div>
              </div>
            );
          })}

          <div className="rv-dist-note">{t.distribution}</div>
        </div>
      </div>

      {canWrite && (
        <div className="rv-mine">
          <div className="rv-mine-top">
            <div className="rv-mine-title">{t.myReview}</div>

            {myReview?.id && (
              <button
                className="rv-btn rv-btn-ghost"
                onClick={handleDelete}
                disabled={deleting}
                type="button"
              >
                {deleting ? t.deleting : t.delete}
              </button>
            )}
          </div>

          <Stars value={draftRating} onChange={setDraftRating} />

          <textarea
            className="rv-textarea"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder={t.placeholder}
            rows={4}
          />

          {error ? <div className="rv-error">{error}</div> : null}

          <div className="rv-actions">
            <button
              className="rv-btn rv-btn-primary"
              onClick={handleSave}
              disabled={saving}
              type="button"
            >
              {saving ? t.saving : t.save}
            </button>
          </div>
        </div>
      )}

      <div className="rv-list">
        {visibleList.length === 0 ? (
          <div className="rv-empty">
            {canManage ? t.noReviews : t.noVisibleReviews}
          </div>
        ) : (
          visibleList.map((r) => {
            const author = r?.author_name || t.user;
            const text = safeText(r);
            const hidden = isHidden(r);

            return (
              <div
                key={r.id}
                className={"rv-card" + (hidden ? " is-hidden" : "")}
              >
                <div className="rv-card-top">
                  <div className="rv-avatar">{author?.[0] || "U"}</div>

                  <div className="rv-meta">
                    <div className="rv-name">{author}</div>
                    <div className="rv-date">
                      {(r.created_at || r.updated_at || "").slice(0, 10)}
                    </div>
                  </div>

                  <div className="rv-right">
                    <Stars value={r?.rating} readOnly />

                    {canManage && (
                      <button
                        className="rv-pill"
                        type="button"
                        onClick={() => handleToggleVisibility(r.id, hidden)}
                        disabled={toggleId === r.id}
                        title={hidden ? t.makeVisible : t.hide}
                      >
                        {hidden ? t.hidden : t.visible}
                      </button>
                    )}
                  </div>
                </div>

                {text ? (
                  <div className="rv-body">{text}</div>
                ) : (
                  <div className="rv-body rv-muted">{t.noComment}</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {lastPage > 1 && (
        <div className="rv-pager">
          <button
            className="rv-btn"
            disabled={!canPrev}
            onClick={() => onPageChange?.(currentPage - 1)}
            type="button"
          >
            {t.prev}
          </button>

          <div className="rv-pager-info">
            {t.page} {currentPage} {t.of} {lastPage}
          </div>

          <button
            className="rv-btn"
            disabled={!canNext}
            onClick={() => onPageChange?.(currentPage + 1)}
            type="button"
          >
            {t.next}
          </button>
        </div>
      )}
    </div>
  );
}